<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import EmailInboxAPI from 'dashboard/api/aceleres/emailInbox';
import EmailRecipientChips from './EmailRecipientChips.vue';
import EmailSnoozeDialog from './EmailSnoozeDialog.vue';
import { useEmailInbox } from 'dashboard/composables/useEmailInbox';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import EmailMessageCard from './EmailMessageCard.vue';
import LabelTagIcon from 'dashboard/components-next/label/LabelTagIcon.vue';

// Aceleres — leitor da aba E-mails: assunto, mensagens da conversa (a última
// aberta, as anteriores recolhidas) e resposta pelo SMTP da própria caixa.
const emit = defineEmits(['open-navigation']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const {
  setThreadUnread,
  replaceThread,
  fetchUnreadCount,
  toggleThreadStar,
  labels,
  threads,
  fetchLabels,
  fetchThreads,
} = useEmailInbox();

const thread = ref(null);
const messages = ref([]);
const expandedIds = ref(new Set());
const isLoading = ref(false);
const notFound = ref(false);
const editorMode = ref(null);
const isSending = ref(false);
const showCc = ref(false);
const replyContent = ref('');
const ccEmails = ref('');
const bccEmails = ref('');
const forwardTo = ref('');
const forwardContent = ref('');
const forwardEditor = ref(null);
const forwardRecipients = ref(null);
const attachedFiles = ref([]);
const fileInput = ref(null);
const snoozeOpen = ref(false);
const snoozeAnchor = ref(null);
const ccRecipients = ref(null);
const bccRecipients = ref(null);
const labelOpen = ref(false);
const selectedLabels = ref([]);
const scrollRef = ref(null);

const threadId = computed(() => Number(route.params.id));
const threadIndex = computed(() =>
  threads.value.findIndex(item => item.id === threadId.value)
);
const previousThread = computed(() =>
  threadIndex.value > 0 ? threads.value[threadIndex.value - 1] : null
);
const nextThread = computed(() =>
  threadIndex.value >= 0 ? threads.value[threadIndex.value + 1] : null
);
const goToThread = item => {
  if (item)
    router.push({
      name: 'inbox_view_email_thread',
      params: { id: item.id },
      query: { tab: 'emails' },
    });
};
const starLabel = computed(() =>
  thread.value?.starred ? t('INBOX.EMAIL.UNSTAR') : t('INBOX.EMAIL.STAR')
);

const backToList = () => {
  router.push({ name: 'inbox_view', query: { tab: 'emails' } });
};

const expandDefaults = list => {
  const ids = new Set();
  const last = list[list.length - 1];
  if (last) ids.add(last.id);
  expandedIds.value = ids;
};

const loadThread = async ({ keepExpanded = false } = {}) => {
  const requestedId = threadId.value;
  if (!requestedId) return;
  if (!keepExpanded) isLoading.value = true;
  notFound.value = false;

  try {
    const { data } = await EmailInboxAPI.show(requestedId);
    if (requestedId !== threadId.value) return;
    thread.value = data.thread;
    messages.value = data.messages || [];
    if (keepExpanded) {
      const last = messages.value[messages.value.length - 1];
      if (last) expandedIds.value = new Set([...expandedIds.value, last.id]);
    } else {
      expandDefaults(messages.value);
    }

    if (data.thread?.unread) {
      try {
        await EmailInboxAPI.markRead(requestedId);
        setThreadUnread(requestedId, false);
        thread.value = { ...thread.value, unread: false };
      } catch {
        // A falha ao atualizar a leitura não impede a visualização do e-mail.
      }
    }
  } catch {
    if (requestedId === threadId.value) {
      thread.value = null;
      messages.value = [];
      notFound.value = true;
    }
  } finally {
    if (requestedId === threadId.value) isLoading.value = false;
  }
};

const toggleMessage = id => {
  const ids = new Set(expandedIds.value);
  if (ids.has(id)) ids.delete(id);
  else ids.add(id);
  expandedIds.value = ids;
};

const markUnread = async () => {
  try {
    await EmailInboxAPI.markUnread(threadId.value);
    setThreadUnread(threadId.value, true);
    useAlert(t('INBOX.EMAIL.MARKED_UNREAD'));
    backToList();
  } catch {
    // mantém o leitor aberto
  }
};

const snoozeThread = async until => {
  try {
    await EmailInboxAPI.snooze(threadId.value, until);
    snoozeOpen.value = false;
    await fetchThreads();
    useAlert(t('INBOX.EMAIL.SNOOZED'));
    backToList();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.SNOOZE_ERROR'));
  }
};

const moveThread = async folder => {
  try {
    await EmailInboxAPI.moveFolder(threadId.value, folder);
    useAlert(t('INBOX.EMAIL.FOLDER_MOVED'));
    backToList();
    fetchUnreadCount();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.FOLDER_ERROR'));
  }
};

const openSnooze = event => {
  const { left, top, bottom } = event.currentTarget.getBoundingClientRect();
  snoozeAnchor.value = { left, top, bottom };
  snoozeOpen.value = true;
};

const openLabels = async () => {
  await fetchLabels();
  selectedLabels.value = [...(thread.value?.labels || [])];
  labelOpen.value = true;
};

const saveLabels = async () => {
  try {
    const { data } = await EmailInboxAPI.updateLabels(
      threadId.value,
      selectedLabels.value
    );
    thread.value = { ...thread.value, labels: data.labels };
    replaceThread(thread.value);
    labelOpen.value = false;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.LABEL_ERROR'));
  }
};

const onEscape = event => {
  if (event.key !== 'Escape') return;
  snoozeOpen.value = false;
  labelOpen.value = false;
};

onMounted(() => document.addEventListener('keydown', onEscape));

const toggleStar = async () => {
  if (!thread.value) return;
  const starred = !thread.value.starred;
  thread.value = { ...thread.value, starred };
  try {
    const persistedStarred = await toggleThreadStar(thread.value.id);
    if (typeof persistedStarred === 'boolean') {
      thread.value = { ...thread.value, starred: persistedStarred };
    }
  } catch {
    thread.value = { ...thread.value, starred: !starred };
    useAlert(t('INBOX.EMAIL.STAR_ERROR'));
  }
};

const resetEditor = () => {
  editorMode.value = null;
  showCc.value = false;
  replyContent.value = '';
  ccEmails.value = '';
  bccEmails.value = '';
  forwardTo.value = '';
  forwardContent.value = '';
  attachedFiles.value = [];
  if (fileInput.value) fileInput.value.value = '';
};

const forwardedMessage = computed(
  () => messages.value[messages.value.length - 1]
);
const forwardedHeader = message => {
  const container = document.createElement('div');
  const heading = document.createElement('p');
  heading.textContent = '---------- Mensagem encaminhada ----------';
  container.append(heading);
  const date = message.sent_at
    ? new Date(message.sent_at * 1000).toLocaleString('pt-BR')
    : '';
  [
    [t('INBOX.EMAIL.FORWARD_FROM'), message.from?.join(', ')],
    [t('INBOX.EMAIL.FORWARD_DATE'), date],
    [
      t('INBOX.EMAIL.FORWARD_SUBJECT'),
      message.subject || thread.value?.subject,
    ],
    [t('INBOX.EMAIL.FORWARD_RECIPIENTS'), message.to?.join(', ')],
  ].forEach(([label, value]) => {
    if (!value) return;
    const line = document.createElement('div');
    line.textContent = `${label}: ${value}`;
    container.append(line);
  });
  container.append(document.createElement('br'));
  const body = document.createElement('div');
  body.innerHTML = DOMPurify.sanitize(message.html || '');
  container.append(body);
  return container.innerHTML;
};

const openEditor = async mode => {
  resetEditor();
  editorMode.value = mode;
  if (mode === 'forward' && forwardedMessage.value) {
    await nextTick();
    if (forwardEditor.value) {
      forwardEditor.value.innerHTML = forwardedHeader(forwardedMessage.value);
      forwardContent.value = forwardEditor.value.innerText.trim();
    }
  }
};

const addFiles = event => {
  const files = [...(event.target.files || [])];
  if (attachedFiles.value.length + files.length > 15) {
    useAlert(t('INBOX.EMAIL.ATTACHMENT_LIMIT'));
  } else {
    attachedFiles.value = [...attachedFiles.value, ...files];
  }
  event.target.value = '';
};

const removeFile = index => {
  attachedFiles.value = attachedFiles.value.filter(
    (_, position) => position !== index
  );
};

const sendReply = async () => {
  if (!replyContent.value.trim() || isSending.value) return;
  if (
    ![ccRecipients.value, bccRecipients.value].every(
      field => !field || field.commit()
    )
  ) {
    useAlert(t('INBOX.EMAIL.INVALID_RECIPIENT'));
    return;
  }
  isSending.value = true;
  try {
    await EmailInboxAPI.reply(threadId.value, {
      content: replyContent.value,
      ccEmails: ccEmails.value,
      bccEmails: bccEmails.value,
      files: attachedFiles.value,
    });
    useAlert(t('INBOX.EMAIL.SENT'));
    resetEditor();
    await loadThread({ keepExpanded: true });
    if (thread.value) replaceThread(thread.value);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

const sendForward = async () => {
  if (isSending.value || !forwardRecipients.value?.commit()) {
    if (!isSending.value) useAlert(t('INBOX.EMAIL.INVALID_RECIPIENT'));
    return;
  }
  const content = forwardEditor.value?.innerText?.trim() || '';
  if (!forwardTo.value || !content) return;
  isSending.value = true;
  try {
    await EmailInboxAPI.compose({
      inboxId: thread.value.inbox_id,
      to: forwardTo.value,
      cc: '',
      bcc: '',
      subject: `Fwd: ${thread.value.subject || t('INBOX.EMAIL.NO_SUBJECT')}`,
      content,
      emailHtmlContent: DOMPurify.sanitize(forwardEditor.value.innerHTML),
      files: attachedFiles.value,
    });
    useAlert(t('INBOX.EMAIL.FORWARDED'));
    resetEditor();
    await fetchThreads();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.EMAIL.FORWARD_ERROR'));
  } finally {
    isSending.value = false;
  }
};

const onRealtimeUpdate = payload => {
  if (
    !thread.value ||
    Number(payload?.conversationId) !== thread.value.conversation_id
  )
    return;
  loadThread({ keepExpanded: true });
};

emitter.on(BUS_EVENTS.ACELERES_EMAIL_INBOX_UPDATED, onRealtimeUpdate);
onUnmounted(() => {
  emitter.off(BUS_EVENTS.ACELERES_EMAIL_INBOX_UPDATED, onRealtimeUpdate);
  document.removeEventListener('keydown', onEscape);
  fetchUnreadCount();
});

watch(
  threadId,
  () => {
    resetEditor();
    loadThread();
    scrollRef.value?.scrollTo?.({ top: 0 });
  },
  { immediate: true }
);
</script>

<template>
  <section class="flex min-h-0 min-w-0 flex-1 flex-col bg-white text-[#202124]">
    <header
      class="flex shrink-0 flex-wrap items-center gap-1 border-b border-[#e0e3e7] bg-white px-3 py-2 md:px-5"
    >
      <button
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4] md:hidden"
        :aria-label="t('INBOX.EMAIL.OPEN_NAVIGATION')"
        @click="emit('open-navigation')"
      >
        <span class="i-lucide-menu size-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
        :aria-label="t('INBOX.EMAIL.BACK')"
        :title="t('INBOX.EMAIL.BACK')"
        @click="backToList"
      >
        <span class="i-lucide-arrow-left size-5" aria-hidden="true" />
      </button>
      <button
        v-if="thread"
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
        :aria-label="t('INBOX.EMAIL.MARK_UNREAD')"
        :title="t('INBOX.EMAIL.MARK_UNREAD')"
        @click="markUnread"
      >
        <span class="i-lucide-mail size-5" aria-hidden="true" />
      </button>
      <button
        v-if="thread"
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
        :aria-label="t('INBOX.EMAIL.SNOOZE')"
        :title="t('INBOX.EMAIL.SNOOZE')"
        @click="openSnooze"
      >
        <span class="i-lucide-clock-3 size-5" aria-hidden="true" />
      </button>
      <button
        v-if="thread"
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
        :aria-label="t('INBOX.EMAIL.LABELS')"
        :title="t('INBOX.EMAIL.LABELS')"
        @click="openLabels"
      >
        <LabelTagIcon :size="20" />
      </button>
      <button
        v-for="action in thread &&
        ['trash', 'spam', 'archived'].includes(thread.folder)
          ? [
              {
                key: 'inbox',
                icon: 'i-lucide-inbox',
                label: t('INBOX.EMAIL.RESTORE'),
              },
            ]
          : thread
            ? [
                {
                  key: 'archived',
                  icon: 'i-lucide-archive',
                  label: t('INBOX.EMAIL.ARCHIVE'),
                },
                {
                  key: 'spam',
                  icon: 'i-lucide-shield-alert',
                  label: t('INBOX.EMAIL.MARK_SPAM'),
                },
                {
                  key: 'trash',
                  icon: 'i-lucide-trash-2',
                  label: t('INBOX.EMAIL.MOVE_TRASH'),
                },
              ]
            : []"
        :key="action.key"
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
        :aria-label="action.label"
        :title="action.label"
        @click="moveThread(action.key)"
      >
        <span :class="action.icon" class="size-5" aria-hidden="true" />
      </button>
      <div class="flex-1" />
      <span
        v-if="threadIndex >= 0"
        class="hidden text-xs text-[#5f6368] sm:inline"
        >{{ threadIndex + 1 }} {{ t('INBOX.EMAIL.OF') }}
        {{ threads.length }}</span
      >
      <button
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4] disabled:opacity-30"
        :aria-label="t('INBOX.EMAIL.PREVIOUS_EMAIL')"
        :disabled="!previousThread"
        @click="goToThread(previousThread)"
      >
        <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4] disabled:opacity-30"
        :aria-label="t('INBOX.EMAIL.NEXT_EMAIL')"
        :disabled="!nextThread"
        @click="goToThread(nextThread)"
      >
        <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
      </button>
    </header>

    <div ref="scrollRef" class="min-h-0 flex-1 overflow-y-auto">
      <div v-if="isLoading" class="flex justify-center py-10">
        <Spinner class="text-n-brand" />
      </div>
      <p
        v-else-if="notFound"
        class="px-6 py-10 text-center text-sm text-n-slate-10"
      >
        {{ t('INBOX.EMAIL.NOT_FOUND') }}
      </p>
      <div
        v-else-if="thread"
        class="mx-auto flex w-full max-w-6xl flex-col gap-3 px-3 py-4 md:px-6"
      >
        <div class="flex items-start gap-3 px-1">
          <h2
            class="flex-1 text-lg font-semibold leading-snug text-n-slate-12 md:text-xl"
          >
            {{ thread.subject || t('INBOX.EMAIL.NO_SUBJECT') }}
          </h2>
          <button
            type="button"
            class="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
            :class="thread.starred ? '!text-[#d99a00]' : ''"
            :aria-label="starLabel"
            :title="starLabel"
            :aria-pressed="thread.starred"
            @click="toggleStar"
          >
            <svg
              class="size-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polygon
                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                :class="thread.starred ? 'fill-current' : ''"
              />
            </svg>
          </button>
          <span
            v-if="thread.inbox_email"
            class="mt-1 hidden flex-shrink-0 rounded-md bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11 md:inline"
          >
            {{ thread.inbox_email }}
          </span>
        </div>

        <EmailMessageCard
          v-for="message in messages"
          :key="message.id"
          :message="message"
          :inbox-email="thread.inbox_email || ''"
          :expanded="expandedIds.has(message.id)"
          @toggle="toggleMessage(message.id)"
        />

        <div
          v-if="!editorMode"
          class="flex flex-wrap gap-2 px-1 md:pl-[4.25rem]"
        >
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
            @click="openEditor('reply')"
          >
            <span class="i-lucide-reply size-4" aria-hidden="true" />
            {{ t('INBOX.EMAIL.REPLY') }}
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1"
            @click="openEditor('forward')"
          >
            <span
              class="i-lucide-forward size-4 md:hidden"
              aria-hidden="true"
            />
            <Icon
              icon="i-tabler-corner-up-right-double"
              class="hidden size-4 md:inline-flex"
              aria-hidden="true"
            />
            {{ t('INBOX.EMAIL.FORWARD') }}
          </button>
        </div>

        <form
          v-else
          class="flex flex-col gap-3 rounded-xl border border-[#dadce0] bg-white p-4 shadow-sm md:ml-[3.25rem]"
          @submit.prevent="editorMode === 'reply' ? sendReply() : sendForward()"
        >
          <div class="flex items-center gap-2 text-sm text-[#5f6368]">
            <span
              v-if="editorMode === 'reply'"
              class="i-lucide-reply size-4 shrink-0"
              aria-hidden="true"
            />
            <template v-else>
              <span
                class="i-lucide-forward size-4 shrink-0 md:hidden"
                aria-hidden="true"
              />
              <Icon
                icon="i-tabler-corner-up-right-double"
                class="hidden size-4 shrink-0 md:inline-flex"
                aria-hidden="true"
              />
            </template>
            <span v-if="editorMode === 'reply'" class="truncate">{{
              thread.sender?.email
            }}</span>
            <span v-else>{{ t('INBOX.EMAIL.FORWARD') }}</span>
            <button
              v-if="editorMode === 'reply' && !showCc"
              type="button"
              class="ml-auto text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
              @click="showCc = true"
            >
              {{ t('INBOX.EMAIL.ADD_CC') }}
            </button>
          </div>
          <EmailRecipientChips
            v-if="editorMode === 'forward'"
            ref="forwardRecipients"
            v-model="forwardTo"
            :label="t('INBOX.EMAIL.COMPOSE_TO')"
            :placeholder="t('INBOX.EMAIL.COMPOSE_TO_MULTIPLE')"
          />
          <template v-if="editorMode === 'reply' && showCc">
            <EmailRecipientChips
              ref="ccRecipients"
              v-model="ccEmails"
              :label="t('INBOX.EMAIL.CC')"
              :placeholder="t('INBOX.EMAIL.CC_PLACEHOLDER')"
            />
            <EmailRecipientChips
              ref="bccRecipients"
              v-model="bccEmails"
              :label="t('INBOX.EMAIL.BCC')"
              :placeholder="t('INBOX.EMAIL.BCC_PLACEHOLDER')"
            />
          </template>
          <textarea
            v-if="editorMode === 'reply'"
            v-model="replyContent"
            rows="6"
            class="min-h-[8rem] w-full resize-y border-0 bg-transparent p-2 text-sm text-[#202124] outline-none"
            :placeholder="t('INBOX.EMAIL.REPLY_PLACEHOLDER')"
          />
          <div
            v-else
            ref="forwardEditor"
            contenteditable="true"
            role="textbox"
            aria-multiline="true"
            :aria-label="t('INBOX.EMAIL.FORWARD_MESSAGE')"
            class="min-h-[12rem] max-h-[32rem] overflow-auto p-2 text-sm text-[#202124] outline-none focus:ring-1 focus:ring-[#16834b] [&_a]:text-[#1a73e8] [&_blockquote]:border-l-2 [&_blockquote]:pl-3"
            @input="forwardContent = $event.target.innerText"
          />
          <div v-if="attachedFiles.length" class="flex flex-wrap gap-2">
            <span
              v-for="(file, index) in attachedFiles"
              :key="`${file.name}-${index}`"
              class="inline-flex max-w-full items-center gap-1 rounded-lg border border-[#dadce0] px-2 py-1 text-xs text-[#3c4043]"
            >
              <span
                class="i-lucide-paperclip size-3.5 shrink-0"
                aria-hidden="true"
              />
              <span class="truncate">{{ file.name }}</span>
              <button
                type="button"
                :aria-label="t('INBOX.EMAIL.REMOVE_ATTACHMENT')"
                @click="removeFile(index)"
              >
                <span class="i-lucide-x size-3.5" aria-hidden="true" />
              </button>
            </span>
          </div>
          <div class="flex items-center gap-2 border-t border-[#e8eaed] pt-3">
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              @change="addFiles"
            />
            <button
              type="submit"
              class="rounded-full bg-[#15603e] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
              :disabled="
                isSending ||
                (editorMode === 'reply'
                  ? !replyContent.trim()
                  : !forwardTo || !forwardContent.trim())
              "
            >
              {{ isSending ? t('INBOX.EMAIL.SENDING') : t('INBOX.EMAIL.SEND') }}
            </button>
            <button
              type="button"
              class="inline-flex size-9 items-center justify-center rounded-full text-[#5f6368] hover:bg-[#f1f3f4]"
              :aria-label="t('INBOX.EMAIL.ADD_ATTACHMENT')"
              :title="t('INBOX.EMAIL.ADD_ATTACHMENT')"
              @click="fileInput?.click()"
            >
              <span class="i-lucide-paperclip size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="ml-auto rounded-full px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2"
              @click="resetEditor"
            >
              {{ t('INBOX.EMAIL.CANCEL') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    <EmailSnoozeDialog
      :open="snoozeOpen"
      :anchor-rect="snoozeAnchor"
      @close="snoozeOpen = false"
      @confirm="snoozeThread"
    />
    <div
      v-if="labelOpen"
      class="fixed inset-0 z-[101] flex items-center justify-center bg-black/30 p-4"
      @click.self="labelOpen = false"
    >
      <div class="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        <h2 class="text-base font-semibold text-[#202124]">
          {{ t('INBOX.EMAIL.LABELS') }}
        </h2>
        <div class="mt-3 max-h-56 space-y-2 overflow-y-auto">
          <label
            v-for="label in labels"
            :key="label.id"
            class="flex items-center gap-2 text-sm text-[#3c4043]"
          >
            <input
              v-model="selectedLabels"
              type="checkbox"
              :value="label.title"
              :disabled="thread?.inherited_labels?.includes(label.title)"
              class="accent-[#15603e]"
            />
            <LabelTagIcon :color="label.color" :size="14" />{{ label.title }}
          </label>
        </div>
        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-full px-4 py-2 text-sm text-[#5f6368]"
            @click="labelOpen = false"
          >
            {{ t('INBOX.EMAIL.CANCEL') }}
          </button>
          <button
            type="button"
            class="rounded-full bg-[#15603e] px-4 py-2 text-sm font-semibold text-white"
            @click="saveLabels"
          >
            {{ t('INBOX.EMAIL.APPLY_LABELS') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
